"use client";

import { useState, useEffect } from "react";
import type { MigrationResponse, GetMigrationByIdHeaders } from "../services/migration/types";
import { getMigrationById } from "../services/migration/migrationById";
import { extractApiErrorMessage } from "../utils/error-handling";

interface UseMigrationDetailsProps {
  migrationId: string;
  accountId: string;
  autoFetch?: boolean;
}

interface UseMigrationDetailsResult {
  migrationData: MigrationResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching and managing migration details
 */
export function useMigrationDetails({
  migrationId,
  accountId,
  autoFetch = true,
}: UseMigrationDetailsProps): UseMigrationDetailsResult {
  const [migrationData, setMigrationData] = useState<MigrationResponse | null>(null);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState<string | null>(null);

  const fetchMigrationDetails = async () => {
    if (!migrationId || !accountId) return;

    try {
      setLoading(true);
      setError(null);

      const headers: GetMigrationByIdHeaders = { accountId };
      const response = await getMigrationById(headers, migrationId);
      if (response.success && response.content) {
        // Transform the response to match the MigrationResponse type
        const transformedResponse: MigrationResponse = {
          ...response,
          error: response.error ? extractApiErrorMessage(response.error) : undefined,
        };
        setMigrationData(transformedResponse);
      } else {
        setError(extractApiErrorMessage(response.error) || "Error fetching migration details");
      }
    } catch (err) {
      console.error("Error in fetchMigrationDetails:", err);
      setError("Unexpected error loading migration details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchMigrationDetails();
    }
  }, [migrationId, accountId, autoFetch]);

  return {
    migrationData,
    loading,
    error,
    refetch: fetchMigrationDetails,
  };
}
